import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Box, Typography, Button, Grid,
  IconButton, Card, CardContent, CardActions, Chip, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField,
  FormControl, InputLabel, Select, MenuItem, CircularProgress, Alert,
  DialogContentText, Tooltip
} from '@mui/material';
import {
  Add as AddIcon, Event as EventIcon, VideoCall as VideoCallIcon,
  Place as PlaceIcon, Edit as EditIcon, Delete as DeleteIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { meetingService, projectService } from '../services/api';

function Meetings() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [meetings, setMeetings] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [open, setOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    start_time: '',
    end_time: '',
    location: '',
    type: 'online',
    meeting_link: '',
    project_id: '',
    attendees: []
  });

  const [projectMembers, setProjectMembers] = useState([]);

  useEffect(() => {
    if (formData.project_id) {
      projectService.getById(formData.project_id)
        .then(res => {
          if (res.data && res.data.project && res.data.project.members) {
            setProjectMembers(res.data.project.members);
          }
        })
        .catch(err => console.error(err));
    } else {
      setProjectMembers([]);
    }
  }, [formData.project_id]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [meetingsRes, projectsRes] = await Promise.all([
        meetingService.getAll(),
        projectService.getAll()
      ]);
      setMeetings(meetingsRes.data);
      setProjects(projectsRes.data.projects || []);
    } catch (err) {
      setError(t('meetings.loadError'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      if (isEditing && selectedMeeting) {
        await meetingService.update(selectedMeeting.id, formData);
      } else {
        await meetingService.create(formData);
      }
      setOpen(false);
      fetchData();
      resetForm();
    } catch (err) {
      setError(t('meetings.saveError'));
    }
  };

  const resetForm = () => {
    setFormData({
      title: '', description: '', start_time: '', end_time: '',
      location: '', type: 'online', meeting_link: '', project_id: '', attendees: []
    });
    setIsEditing(false);
    setSelectedMeeting(null);
  };

  const handleOpenCreate = () => {
    resetForm();
    setOpen(true);
  };

  const handleEdit = (meeting) => {
    setSelectedMeeting(meeting);
    const formatForInput = (dateStr) => {
        if (!dateStr) return '';
        return dateStr.replace(' ', 'T').slice(0, 16);
    };

    setFormData({
      title: meeting.title,
      description: meeting.description || '',
      start_time: formatForInput(meeting.start_time),
      end_time: formatForInput(meeting.end_time),
      location: meeting.location || '',
      type: meeting.type,
      meeting_link: meeting.meeting_link || '',
      project_id: meeting.project_id || '',
      attendees: []
    });
    setIsEditing(true);
    setOpen(true);
  };

  const handleDeleteClick = (meeting) => {
    setSelectedMeeting(meeting);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      if (selectedMeeting) {
        await meetingService.delete(selectedMeeting.id);
        setDeleteDialogOpen(false);
        fetchData();
      }
    } catch (err) {
      setError(t('meetings.deleteError'));
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString(undefined, {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 2, md: 4 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 2, md: 4 }, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h4" component="h1" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
            {t('meetings.title')}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
            sx={{ borderRadius: 2 }}
          >
            {t('meetings.newMeeting')}
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {meetings.length === 0 ? (
                <Grid item xs={12}>
                    <Typography variant="body1" align="center" color="text.secondary">
                        {t('meetings.noMeetings')}
                    </Typography>
                </Grid>
            ) : (
                meetings.map((meeting) => (
                <Grid item xs={12} md={6} lg={4} key={meeting.id}>
                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 2 }}>
                    <CardContent sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                            <Chip
                                label={meeting.type === 'online' ? t('meetings.online') : t('meetings.inPerson')}
                                color={meeting.type === 'online' ? 'primary' : 'secondary'}
                                size="small"
                            />
                            {meeting.project_title && (
                                <Chip label={meeting.project_title} variant="outlined" size="small" />
                            )}
                        </Box>
                        <Typography variant="h6" component="h2" gutterBottom fontWeight="bold">
                        {meeting.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EventIcon fontSize="small" />
                        {formatDate(meeting.start_time)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>
                        {meeting.description}
                        </Typography>
                        {meeting.location && (
                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PlaceIcon fontSize="small" /> {meeting.location}
                            </Typography>
                        )}
                    </CardContent>
                    <CardActions sx={{ p: 2, pt: 0, justifyContent: 'space-between' }}>
                        {meeting.meeting_link && (
                        <Button
                            size="small"
                            startIcon={<VideoCallIcon />}
                            href={meeting.meeting_link}
                            target="_blank"
                            variant="outlined"
                        >
                            {t('meetings.join')}
                        </Button>
                        )}
                        <Box>
                          <Tooltip title={t('common.edit')}>
                            <IconButton size="small" onClick={() => handleEdit(meeting)}>
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('common.delete')}>
                            <IconButton size="small" color="error" onClick={() => handleDeleteClick(meeting)}>
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                    </CardActions>
                    </Card>
                </Grid>
                ))
            )}
          </Grid>
        )}

        <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{isEditing ? t('meetings.editMeeting') : t('meetings.newMeeting')}</DialogTitle>
          <DialogContent>
            <Box component="form" sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label={t('common.title')}
                fullWidth
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
              <TextField
                label={t('common.description')}
                fullWidth
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
              <FormControl fullWidth>
                <InputLabel>{t('meetings.project')}</InputLabel>
                <Select
                  value={formData.project_id}
                  label={t('meetings.project')}
                  onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
                >
                  <MenuItem value=""><em>{t('common.none')}</em></MenuItem>
                  {projects.map((p) => (
                    <MenuItem key={p.id} value={p.id}>{p.title}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {formData.project_id && (
                <FormControl fullWidth>
                  <InputLabel>{t('meetings.attendees')}</InputLabel>
                  <Select
                    multiple
                    value={formData.attendees}
                    label={t('meetings.attendees')}
                    onChange={(e) => setFormData({ ...formData, attendees: e.target.value })}
                  >
                    {projectMembers.map((member) => (
                      <MenuItem key={member.id} value={member.id}>
                        {member.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label={t('meetings.startTime')}
                    type="datetime-local"
                    fullWidth
                    required
                    InputLabelProps={{ shrink: true }}
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label={t('meetings.endTime')}
                    type="datetime-local"
                    fullWidth
                    required
                    InputLabelProps={{ shrink: true }}
                    value={formData.end_time}
                    onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                  />
                </Grid>
              </Grid>
              <FormControl fullWidth>
                <InputLabel>{t('common.type')}</InputLabel>
                <Select
                  value={formData.type}
                  label={t('common.type')}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                >
                  <MenuItem value="online">{t('meetings.online')}</MenuItem>
                  <MenuItem value="in_person">{t('meetings.inPerson')}</MenuItem>
                </Select>
              </FormControl>
              {formData.type === 'online' ? (
                <TextField
                  label={t('meetings.meetingLink')}
                  fullWidth
                  value={formData.meeting_link}
                  onChange={(e) => setFormData({ ...formData, meeting_link: e.target.value })}
                />
              ) : (
                <TextField
                  label={t('common.location')}
                  fullWidth
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                />
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleSubmit} variant="contained">
              {isEditing ? t('common.update') : t('common.create')}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>{t('common.confirmDelete')}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t('meetings.deleteConfirmText')}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleDeleteConfirm} color="error" autoFocus>
              {t('common.delete')}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
}

export default Meetings;
